import React from 'react';
import {
  Checkbox, CogIcon,
  DocumentShareIcon,
  DownloadIcon,
  IconButton,
  Pane, Popover,
  Position,
  RepeatIcon,
  SearchIcon,
  Select, SeriesAddIcon,
  Text, ThIcon, TimelineLineChartIcon, toaster,
  Tooltip,
} from 'evergreen-ui';
import { useUrlParams } from 'Utils/useUrlParams';
import { DAY, DECADE, MONTH, PERIOD_DICTIONARY, WEEK, YEAR } from 'Utils/Pagination/usePagination';
import { timeZoneToUTC } from 'Utils/UTCConverter';
import { DATE_FORMATS } from 'Utils/formats';
import { useFormingReportCSVMutation } from 'Services/api/reportsApi';
import { StartEndDate } from 'Shared/StartEndDate';
import { CheckboxList } from 'Shared/CheckboxList2';
import { TimeseriesPanelContext } from './Timeseries.Panel.Context';
import { selectableConfines, VIEW_GRAPH, VIEW_TABLE } from './Timeseries.Utils';
import { TimeseriesContext } from './Timeseries.Context';
import PDFGraphsCreator from "../../Utils/PDFCreator";
import {getDatasourceById, getStationByDatasourceId, getStationById} from "./TimeseriesHelperApi";

export default function TimeseriesPanel({ children }) {
  const {
    selectedMeteocodeFilter,
    localDateTime,
    localPageSize,
    localCalcParams,
    selectedMeteocodes,
    selectedConfines,
    setLocalDateTime,
    setLocalPageSize,
  } = React.useContext(TimeseriesPanelContext);

  const { isFixedPanelParams } = React.useContext(TimeseriesContext);

  const [, updateParams, setParams] = useUrlParams();

  const onChangeDate = ({ name, value }) => {
    setLocalDateTime((cur) => ({
      ...cur,
      [name]: value,
    }));
  };

  const onChangePageSize = (e) => {
    const { value } = e.target;
    setLocalPageSize(value);
  };

  const clearFilter = () => {
    setParams({
      view: VIEW_TABLE,
    });
  };

  const searchClick = () => {
    const params = {
      startDate: localDateTime.startDate,
      endDate: localDateTime.endDate,
      pageSize: localPageSize,
      page: 0,
      meteocodeFilter: selectedMeteocodeFilter,
      ...localCalcParams,
    };

    if (selectedMeteocodes.length > 0) {
      params.meteocodes = selectedMeteocodes.map((meteocode) => meteocode.selectedName);
    } else {
      params.meteocodes = [];
    }

    if (selectedConfines.length > 0) {
      params.confines = selectedConfines.map((confine) => confine.value);
    } else {
      params.confines = [];
    }
    updateParams(params);
  };

  return (
    <Pane display="grid" gridAutoFlow="column" gridAutoColumns="max-content" columnGap=".5rem" justifyContent="center" alignItems="center">
      <StartEndDate
        startDate={localDateTime.startDate}
        endDate={localDateTime.endDate}
        overriddenStartName="startDate"
        overriddenEndName="endDate"
        updateDate={onChangeDate}
        disabled={isFixedPanelParams}
      />
      <Text> показывать за </Text>
      <Select value={localPageSize} onChange={onChangePageSize} disabled={isFixedPanelParams}>
        <option value={DAY}>{PERIOD_DICTIONARY[DAY]}</option>
        <option value={WEEK}>{PERIOD_DICTIONARY[WEEK]}</option>
        <option value={DECADE}>{PERIOD_DICTIONARY[DECADE]}</option>
        <option value={MONTH}>{PERIOD_DICTIONARY[MONTH]}</option>
        <option value={YEAR}>{PERIOD_DICTIONARY[YEAR]}</option>
      </Select>
      {children}
      <Tooltip content="Сбросить фильры" position={Position.TOP}>
        <IconButton icon={RepeatIcon} appearance="primary" intent="danger" onClick={clearFilter} disabled={isFixedPanelParams} />
      </Tooltip>
      <IconButton icon={SearchIcon} appearance="primary" intent="success" onClick={searchClick} disabled={isFixedPanelParams} />
    </Pane>
  );
}

function ExportSelector() {
  const { view, columns, calcModuleParams, date, zone } = React.useContext(TimeseriesContext);
  const [formingCsv] = useFormingReportCSVMutation();

  const [station, setStation] = React.useState(undefined);
  const [datasource, setDatasource] = React.useState(undefined);

  React.useEffect(() => {
    if (columns.length === 0) {
      return;
    }

    if (columns[0].sourceType === 'STATION') {
      setDatasource(undefined)
      const stationId = columns[0].sourceId
      getStationById(stationId).then((resp) => {
        setStation(resp.data)
      })
      return;
    }

    if (columns[0].sourceType === 'DATASOURCE') {
      const datasourceId = columns[0].sourceId
      getDatasourceById(datasourceId).then((resp) => {
        setDatasource(resp.data)
      })
      getStationByDatasourceId(datasourceId).then((resp) => {
        setStation(resp.data)
      })
    }
  }, [columns])
  const onClick = async () => {
    if (view === VIEW_TABLE) {
      if (columns.length > 0) {
        const body = {
          startDate: timeZoneToUTC(new Date(date.startDate), zone, DATE_FORMATS.api),
          endDate: timeZoneToUTC(new Date(date.endDate), zone, DATE_FORMATS.api),
          columns,
          timezone: zoneToInt(zone)
        };
        await formingCsv({body}).unwrap();
        toaster.success('CSV файл формируется...');
      } else {
        toaster.danger('Не выбраны измерения');
      }
    } else {
      await PDFGraphsCreator(
          date,
          station,
          datasource,
          createPdfName(station, datasource, date)
      )
    }
  };

  return (
    <Tooltip content={`Экспорт в ${view === VIEW_TABLE ? 'CSV' : 'PDF'}`}>
      <IconButton
        icon={view === VIEW_TABLE ? DownloadIcon : DocumentShareIcon}
        disabled={false}
        intent="success"
        appearance="primary"
        onClick={onClick}
      />
    </Tooltip>
  );
}

function zoneToInt(zone) {
  let factor = 1;
  if (zone.startsWith("-")) {
    factor = -1;
    zone = zone.substring(1, 3)
  } else if (zone.startsWith("+")) {
    factor = 1;
    zone = zone.substring(1, 3)
  } else {
    zone = zone.substring(0, 2)
  }
  let intZone = parseInt(zone, 10)
  return intZone * factor
}

function createPdfName(station, datasource, date) {
  const divider = " "
  const idx = station.idx
  const name = station.name
  const datasourceLine = datasource === undefined ?
    "" :
    datasource.datasourceType.kod
  const start = date.startDate.split("T")[0]
  const end = date.endDate.split("T")[0]
  return clearString(idx) + divider
    + clearString(name) + divider
    + clearString(datasourceLine) + divider
    + "(" + start
    + "-" + end
    + ")"
}

function clearString(string) {
  return string.replace(/[<>:\\"/|?*]/g, "")
}

function ChangeViewSelector() {
  const { view } = React.useContext(TimeseriesContext);
  const [, updateParams] = useUrlParams();

  const onChangeView = () => {
    updateParams({
      view: view === VIEW_TABLE ? VIEW_GRAPH : VIEW_TABLE,
      confines: [],
    });
  };

  return (
    <Tooltip content={`Переключится на ${view === VIEW_TABLE ? 'графики' : 'таблицу'}`}>
      <IconButton
        icon={view === VIEW_TABLE ? TimelineLineChartIcon : ThIcon}
        intent="success"
        appearance="primary"
        onClick={onChangeView}
      />
    </Tooltip>
  );
}

function CalcParamsSelector() {
  const { isFixedPanelParams, calcModuleParams } = React.useContext(TimeseriesContext);
  const { localCalcParams, setLocalCalcParams } = React.useContext(TimeseriesPanelContext);

  const onChangeCalcModuleParams = (e) => {
    const { name, checked } = e.target;
    setLocalCalcParams((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      if (name === 'factData' && checked) {
        const changed = Object.keys(tmp).reduce((acc, item) => {
          if (item === 'factData' || item === 'includeOffGridData') {
            acc[item] = true;
          } else {
            acc[item] = false;
          }
          return acc;
        }, {});
        return changed;
      }
      return {
        ...cur,
        [name]: checked,
      };
    });
  };

  const params = React.useMemo(() => {
    if (isFixedPanelParams) return calcModuleParams;
    return localCalcParams;
  }, [calcModuleParams, isFixedPanelParams, localCalcParams]);

  return (
    <Popover content={(
      <Pane padding=".5em">
        <Checkbox
          name="round"
          label="Округлить значения"
          checked={params?.round}
          onChange={onChangeCalcModuleParams}
          disabled={isFixedPanelParams}
        />
        <Checkbox
          name="includeOffGridData"
          label="Включить значения вне сетки"
          checked={params?.includeOffGridData}
          onChange={onChangeCalcModuleParams}
          disabled={isFixedPanelParams || params?.factData}
        />
        <Checkbox
          name="calc"
          label="Расчитать недостающие значения"
          checked={params?.calc}
          onChange={onChangeCalcModuleParams}
          disabled={isFixedPanelParams || params?.factData}
        />
        <Checkbox
          name="factData"
          label="Фактические сроки"
          checked={params?.factData}
          onChange={onChangeCalcModuleParams}
          disabled={isFixedPanelParams}
        />
      </Pane>
      )}
    >
      <Tooltip
        content="Настройка расчета данных"
        position={Position.TOP}
      >
        <IconButton
          icon={CogIcon}
        />
      </Tooltip>
    </Popover>
  );
}

function ConfinesSelector() {
  const { view } = React.useContext(TimeseriesContext);
  const { selectedConfines, setSelectedConfines } = React.useContext(TimeseriesPanelContext);

  if (view !== VIEW_GRAPH) return null;

  return (
    <Popover content={(
      <Pane margin={8} height={280} overflow="hidden" display="grid" width={600}>
        <CheckboxList
          values={selectableConfines}
          checkedValues={selectedConfines}
          setCheckedValues={setSelectedConfines}
          checkProp="value"
          labelProp="label"
        />
      </Pane>
    )}
    >
      <Tooltip content="Пределы (на графиках)">
        <IconButton
          icon={SeriesAddIcon}
          intent="none"
          appearance="default"
        />
      </Tooltip>
    </Popover>
  );
}

export const Modules = {
  ExportSelector,
  ChangeViewSelector,
  CalcParamsSelector,
  ConfinesSelector,
};
